/**
 * Group page (#/group) notification-group share modal (FE-G1).
 * Distinct from overview share chrome — deep-link group page only.
 */

import {
  formatGroupShareApplyError,
  isGroupShareDbBusyError,
} from "./group-share-busy-ux.js";

/**
 * @param {object} ctx
 * @returns {{ openShareModal: () => Promise<void>, closeShareModal: () => void, wireOpenButton: () => void }}
 */
export function createGroupPageShareChrome(ctx) {
  const {
    view,
    routeSeq,
    api,
    getMe,
    toast,
    escapeHtml,
    $,
    $$,
    setChildMarkup,
    isRouteCurrent,
    canOpenGroupShare,
    groupDisplayForShareApi,
    effectiveOwnerForGroupShareApi,
    buildAuthUsersPath,
    ADMIN_CONSOLE_TIMEOUT_MS,
    buildAdminNotificationGroupSharePreviewPath,
    HOME_CONSOLE_TIMEOUT_MS,
    fetchGroupShareGrantees,
    granteePermissionSummary,
    syncGroupShareGrantees,
    bustDeviceListCaches,
  } = ctx;

  let overlay = null;
  let initialGrantees = new Set();
  let granteesLoadOk = true;

  const closeShareModal = () => {
    if (overlay) {
      try {
        overlay.classList.remove("is-open");
        overlay.setAttribute("aria-hidden", "true");
        overlay.hidden = true;
      } catch (_) {}
      overlay.remove();
      overlay = null;
    }
  };

  const openShareModal = async () => {
    if (!canOpenGroupShare()) return;
    closeShareModal();
    const gd = groupDisplayForShareApi();
    initialGrantees = new Set();
    overlay = document.createElement("div");
    overlay.id = "grpSgModal";
    // .is-open required — polish CSS forces .grp-modal:not(.is-open) { display:none !important }
    overlay.className = "grp-modal is-open";
    overlay.setAttribute("aria-hidden", "false");
    overlay.style.cssText = "display:flex;align-items:center;justify-content:center;padding:12px";
    overlay.innerHTML = `
      <div class="grp-modal-card" id="grpSgModalCard" style="max-width:560px;width:min(560px,96vw)">
        <h3 style="margin:0 0 8px">Share notification group</h3>
        <p class="muted" id="grpSgHint" style="margin:0 0 10px">"${escapeHtml(gd)}" — shared users are pre-checked; uncheck to revoke this group only for them. Pick permissions for newly checked users, then Apply.</p>
        <strong style="display:block;margin:0 0 4px">Boards in group</strong>
        <div id="grpSgPreview" class="grp-pick-list" style="max-height:200px;overflow:auto;margin-bottom:12px">
          <p class="muted">Loading…</p>
        </div>
        <div class="row" style="justify-content:space-between;align-items:center">
          <strong>Users</strong>
          <label class="muted"><input type="checkbox" id="grpSgSelAllUsers" /> Select all</label>
        </div>
        <div id="grpSgUsers" class="grp-pick-list" style="max-height:220px;overflow:auto;margin-bottom:10px">
          <p class="muted">Loading…</p>
        </div>
        <div class="row" style="gap:14px;margin-bottom:8px">
          <label><input type="checkbox" id="grpSgPermView" checked /> View</label>
          <label><input type="checkbox" id="grpSgPermOperate" /> Operate</label>
          <p class="muted" style="margin:8px 0 0;font-size:12px;line-height:1.45">
            Alarm ON/OFF: Alert access or Operate share.
            Group Settings sync: Operate on all boards in this group.
          </p>
        </div>
        <p class="muted" id="grpSgStat" style="min-height:1.2em;margin:0 0 8px"></p>
        <div class="row" style="justify-content:flex-end;gap:8px">
          <button class="btn sm secondary" type="button" id="grpSgRetryGrantees" style="display:none">Retry loading members</button>
          <button class="btn sm secondary" type="button" id="grpSgCancel">Cancel</button>
          <button class="btn sm" type="button" id="grpSgApply">Apply sharing</button>
        </div>
      </div>`;
    overlay.addEventListener("click", (ev) => {
      if (ev.target === overlay) closeShareModal();
    });
    view.appendChild(overlay);

    const me = getMe();
    const ownerForApi = effectiveOwnerForGroupShareApi();
    if (me && me.role === "superadmin" && !ownerForApi) {
      toast("Cannot open group share: open this group from an account that owns the boards, or pick one account scope first.", "err");
      closeShareModal();
      return;
    }

    const previewEl = $("#grpSgPreview", overlay);
    const usersEl = $("#grpSgUsers", overlay);
    const statEl = $("#grpSgStat", overlay);
    const applyBtn = () => $("#grpSgApply", overlay);
    const retryBtn = () => $("#grpSgRetryGrantees", overlay);

    const setGranteesLoadUi = (ok, errMsg) => {
      granteesLoadOk = ok;
      const ab = applyBtn();
      const rb = retryBtn();
      if (ab) ab.disabled = !ok;
      if (rb) rb.style.display = ok ? "none" : "";
      if (statEl) {
        statEl.innerHTML = ok
          ? ""
          : `<p class="badge revoked" style="margin:0">${escapeHtml(errMsg || "Could not load who already has access — Apply stays off.")}</p>`;
      }
    };

    const populateGranteesFromActive = (active) => {
      initialGrantees = new Set();
      for (const it of active) {
        const gu = String(it.grantee_username || "").trim();
        if (gu) initialGrantees.add(gu);
      }
      const perms = granteePermissionSummary(active);
      const pv = $("#grpSgPermView", overlay);
      const po = $("#grpSgPermOperate", overlay);
      if (pv) pv.checked = perms.can_view;
      if (po) po.checked = perms.can_operate;
    };

    const renderShareUserRows = (shareUsers) => {
      if (!usersEl) return;
      setChildMarkup(
        usersEl,
        shareUsers.map((x) => {
          const uname = String(x.username || "").trim();
          const role = escapeHtml(x.role || "user");
          const em = String(x.email || "").trim();
          const emailBit = em ? ` <span class="mono muted">${escapeHtml(em)}</span>` : "";
          const ck = initialGrantees.has(uname) ? " checked" : "";
          return `<label class="grp-pick-item"><input type="checkbox" class="grpSgUsrCb" value="${escapeHtml(uname)}"${ck} /> <span>${escapeHtml(uname)} <span class="mono">(${role})</span>${emailBit}</span></label>`;
        }).join("") || `<p class="muted">No active admin/user accounts.</p>`,
      );
    };

    const loadGranteesForModal = async (shareUsers) => {
      if (me && me.role === "superadmin" && !ownerForApi) {
        setGranteesLoadUi(true, "");
        return;
      }
      setGranteesLoadUi(true, "");
      try {
        const active = await fetchGroupShareGrantees({
          group_display: gd,
          owner_admin: me && me.role === "superadmin" && ownerForApi ? ownerForApi : undefined,
          timeoutMs: 16000,
        });
        if (!isRouteCurrent(routeSeq)) {
          closeShareModal();
          return;
        }
        populateGranteesFromActive(active);
        setGranteesLoadUi(true, "");
      } catch (e) {
        if (!isRouteCurrent(routeSeq)) {
          closeShareModal();
          return;
        }
        initialGrantees = new Set();
        setGranteesLoadUi(false, String(e.message || e));
        toast("Could not load who already has access — Apply stays off until retry works.", "warn");
      }
      renderShareUserRows(shareUsers);
    };
    if (previewEl) {
      setChildMarkup(previewEl, `<p class="muted">Loading group boards…</p>`);
      try {
        const pr = await api(buildAdminNotificationGroupSharePreviewPath({
          group_display: gd,
          owner_admin: me && me.role === "superadmin" && ownerForApi ? ownerForApi : undefined,
        }), { timeoutMs: HOME_CONSOLE_TIMEOUT_MS });
        if (!isRouteCurrent(routeSeq)) {
          closeShareModal();
          return;
        }
        const chips = (pr.items || []).map((it) => {
          const nm = String(
            (it.display_label_short != null && it.display_label_short !== "")
              ? it.display_label_short
              : (it.display_label || it.device_id || ""),
          ).trim();
          const tail = String(it.device_id_tail || "").trim();
          const line = tail ? `${nm} · …${tail}` : nm;
          return `<span class="chip" style="margin:2px">${escapeHtml(line)}</span>`;
        }).join("");
        const cnt = Number(pr.device_count != null ? pr.device_count : (pr.items || []).length);
        setChildMarkup(
          previewEl,
          `<div style="display:flex;flex-wrap:wrap;gap:4px;align-items:center">${chips || "<span class=\"muted\">No boards in this group.</span>"}</div>` +
            `<p class="muted" style="margin:8px 0 0">${cnt} board(s) will receive shared access.</p>`,
        );
      } catch (e) {
        if (!isRouteCurrent(routeSeq)) {
          closeShareModal();
          return;
        }
        setChildMarkup(previewEl, `<p class="badge revoked">${escapeHtml(e.message || e)}</p>`);
      }
    }
    if (usersEl) {
      setChildMarkup(usersEl, `<p class="muted">Loading users…</p>`);
      try {
        const u = await api(buildAuthUsersPath(), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS });
        if (!isRouteCurrent(routeSeq)) {
          closeShareModal();
          return;
        }
        const shareUsers = (u.items || []).filter((x) => {
          const role = String(x.role || "");
          const st = String(x.status || "active");
          if (!(st === "active" || st === "")) return false;
          if (me && me.role === "admin") return role === "user";
          return role === "admin" || role === "user";
        });
        if (!(me && me.role === "superadmin" && !ownerForApi)) {
          await loadGranteesForModal(shareUsers);
        } else {
          renderShareUserRows(shareUsers);
        }
      } catch (e) {
        if (!isRouteCurrent(routeSeq)) {
          closeShareModal();
          return;
        }
        setChildMarkup(usersEl, `<p class="badge revoked">${escapeHtml(e.message || e)}</p>`);
      }
    }
    if (!isRouteCurrent(routeSeq)) {
      closeShareModal();
      return;
    }
    const selAll = $("#grpSgSelAllUsers", overlay);
    if (selAll) {
      selAll.checked = false;
      selAll.onchange = () => {
        $$(".grpSgUsrCb", overlay).forEach((x) => { x.checked = !!selAll.checked; });
      };
    }
    $("#grpSgCancel", overlay)?.addEventListener("click", closeShareModal);
    retryBtn()?.addEventListener("click", async () => {
      if (!usersEl) return;
      const u = await api(buildAuthUsersPath(), { timeoutMs: ADMIN_CONSOLE_TIMEOUT_MS });
      if (!isRouteCurrent(routeSeq)) {
        closeShareModal();
        return;
      }
      const shareUsers = (u.items || []).filter((x) => {
        const role = String(x.role || "");
        const st = String(x.status || "active");
        if (!(st === "active" || st === "")) return false;
        if (me && me.role === "admin") return role === "user";
        return role === "admin" || role === "user";
      });
      setChildMarkup(usersEl, `<p class="muted">Retrying member list…</p>`);
      await loadGranteesForModal(shareUsers);
    });
    applyBtn()?.addEventListener("click", async () => {
      if (!granteesLoadOk) {
        toast("Cannot apply — member list unknown. Use Retry loading members.", "err");
        return;
      }
      const usernames = $$(".grpSgUsrCb", overlay).filter((x) => x.checked).map((x) => String(x.value || "").trim()).filter(Boolean);
      const canView = !!$("#grpSgPermView", overlay)?.checked;
      const canOperate = !!$("#grpSgPermOperate", overlay)?.checked;
      const checkedNow = new Set(usernames);
      const initial = initialGrantees instanceof Set ? initialGrantees : new Set();
      if (checkedNow.size === 0 && initial.size === 0) {
        toast("No changes — check users to share or uncheck to revoke this group.", "warn");
        return;
      }
      if (checkedNow.size > 0 && !canView && !canOperate) {
        toast("Select at least one permission for users who remain shared.", "err");
        return;
      }
      const gNow = String(groupDisplayForShareApi() || "").trim();
      if (!gNow) { toast("Missing group", "err"); return; }
      const ownerNow = effectiveOwnerForGroupShareApi();
      if (me && me.role === "superadmin" && !ownerNow) {
        toast("Cannot apply: pick an account that owns these boards first.", "err");
        return;
      }
      const grantees = [...checkedNow].map((uu) => ({
        grantee_username: uu,
        can_view: canView,
        can_operate: canOperate,
      }));
      if (statEl) statEl.textContent = `Updating group share (${grantees.length} people)…`;
      try {
        const res = await syncGroupShareGrantees({
          group_display: gNow,
          owner_admin: me && me.role === "superadmin" && ownerNow ? ownerNow : undefined,
          grantees,
          timeoutMs: 45000,
        });
        const rev = Number(res.revoked_count || 0);
        const up = Number(res.upserted_count || 0);
        toast(
          `Group share updated · removed ${rev}, shared ${up}. Shared users can open Groups → Shared with me in a few seconds.`,
          "ok",
        );
        closeShareModal();
        try { bustDeviceListCaches(); } catch (_) {}
      } catch (e) {
        const msg = formatGroupShareApplyError(e);
        const busy = isGroupShareDbBusyError(e);
        if (statEl) statEl.textContent = busy ? `${msg} (Apply not saved.)` : msg;
        toast(msg, busy ? "warn" : "err");
        if (!busy) {
          try { bustDeviceListCaches(); } catch (_) {}
        }
      }
      if (!isRouteCurrent(routeSeq)) closeShareModal();
    });
  };

  const wireOpenButton = () => {
    const btn = $("#grpOpenGroupShare", view);
    if (btn) btn.addEventListener("click", () => { openShareModal(); });
  };

  return { openShareModal, closeShareModal, wireOpenButton };
}
